import { Box, Grid, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import { formatDateTimeReadable, formatMoney } from '@nemuru-eng/js-api-client';

import { useWidth } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useWidth';
import { BnplPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Bnpl/_shared/entities/PaymentMethodDetail/BnplPaymentMethodDetailEntity';
import { OrderActionDetail } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Bnpl/OrderActivity/components/OrderActionDetail';
import { RefundButton } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Bnpl/OrderActivity/components/RefundButton';
import { CustomDivider } from '@/components/shared/components/CustomDivider';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';
import { useScreen } from '@/hooks/useScreen';

interface OrderActivityProps {
  paymentMethodDetailEntity: BnplPaymentMethodDetailEntity;
}

export const OrderActivity = ({ paymentMethodDetailEntity }: OrderActivityProps) => {
  const { sm } = useScreen();
  const { ref, width } = useWidth<HTMLDivElement>();

  const order = paymentMethodDetailEntity.order;
  const orderActions = paymentMethodDetailEntity.getOrderActions();

  if (!order) {
    return null;
  }

  const showActivity =
    (paymentMethodDetailEntity.isPartiallyCaptured() || paymentMethodDetailEntity.isCaptured()) &&
    orderActions &&
    orderActions.length > 0;

  if (!showActivity) {
    return null;
  }

  const currency = order.currency;
  const isSmall = !sm || (width && width < 600);

  return (
    <div ref={ref}>
      <CustomPaper fill="paper">
        <FlexRow height={36} mb={2} alignItems="flex-start">
          <Font variant="subtitle1" color="title">
            <Text strong tId="CAPTURES_AND_REFUNDS" />
          </Font>
          <RefundButton paymentMethodDetailEntity={paymentMethodDetailEntity} />
        </FlexRow>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={isSmall ? 12 : 5}>
            <CustomPaper fill="grey.light" style={{ padding: '24px 16px' }}>
              <Box mb={2}>
                <Font variant="h5" color="title">
                  <Text strong tId="SUMMARY" />
                </Font>
              </Box>
              <FlexColumn>
                <FlexRow width={'100%'}>
                  <Font className="overflow-text-ellipsis pr-3" variant={'body2'}>
                    <Text tId={'TOTAL_ORDER'} />
                  </Font>
                  <Font variant={'body2'}>{formatMoney({ value: order.totalOrderAmount, currency })}</Font>
                </FlexRow>
                <FlexRow width={'100%'}>
                  <Font className="overflow-text-ellipsis pr-3" variant={'body2'}>
                    <Text tId={'PENDING_TO_CAPTURE'} />
                  </Font>
                  <Font variant={'body2'}>
                    {formatMoney({
                      value: order.totalOrderAmount - order.totalCapturedAmount,
                      currency,
                    })}
                  </Font>
                </FlexRow>
                <CustomDivider dashed margin={1} />
                <FlexRow width="100%">
                  <Font className="overflow-text-ellipsis pr-3" variant={'body2'}>
                    <Text tId={'TOTAL_CAPTURED'} />
                  </Font>
                  <Font variant={'body2'}>{formatMoney({ value: order.totalCapturedAmount, currency })}</Font>
                </FlexRow>
                <FlexRow width="100%">
                  <Font className="overflow-text-ellipsis pr-3" variant={'body2'}>
                    <Text tId={'TOTAL_REFUNDED'} />
                  </Font>
                  <Font variant={'body2'}>
                    {formatMoney({
                      value: order.totalRefundedAmount > 0 ? -1 * order.totalRefundedAmount : order.totalRefundedAmount,
                      currency,
                    })}
                  </Font>
                </FlexRow>
                <CustomDivider dashed margin={1} />
                <FlexRow width="100%">
                  <Font className="overflow-text-ellipsis pr-3" variant={'body2'}>
                    <Text tId={'BILLED'} />
                  </Font>
                  <Font variant={'body2'}>
                    {formatMoney({
                      value: order.totalCapturedAmount - order.totalRefundedAmount,
                      currency,
                    })}
                  </Font>
                </FlexRow>
                <FlexRow width="100%">
                  <Font className="overflow-text-ellipsis pr-3" variant={'body2'}>
                    <Text tId={'TOTAL_PAID'} />
                  </Font>
                  <Font variant={'body2'}>{formatMoney({ value: order.totalPaidAmount, currency })}</Font>
                </FlexRow>
              </FlexColumn>
            </CustomPaper>
          </Grid>
          <Grid item xs={12} sm={isSmall ? 12 : 7}>
            <CustomPaper fill="inherit" style={{ padding: isSmall ? '24px 0px' : '24px 16px' }} elevation={0}>
              <Box mb={'14px'}>
                <Font variant="h5" color="title">
                  <Text strong tId="TRANSACTION_HISTORY" />
                </Font>
              </Box>
              <Table style={{ width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ padding: '0 0 8px 0' }}>
                      <Text tId="DATE" />
                    </TableCell>
                    <TableCell style={{ padding: '0 0 8px 0' }}>
                      <Text tId="TYPE" />
                    </TableCell>
                    <TableCell style={{ padding: '0 0 8px 0', textAlign: 'right' }}>
                      <Text tId="AMOUNT" />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderActions.map((action) => (
                    <TableRow key={action.id}>
                      <TableCell style={{ padding: '8px 0' }}>
                        <Box pr={1}>{formatDateTimeReadable(action.createdAt)}</Box>
                      </TableCell>
                      <TableCell style={{ padding: '8px 0' }}>
                        <Text tId={action.type} />
                        <OrderActionDetail action={action} />
                      </TableCell>
                      <TableCell style={{ padding: '8px 0', textAlign: 'right' }}>
                        {formatMoney(action.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CustomPaper>
          </Grid>
        </Grid>
      </CustomPaper>
    </div>
  );
};
